// /ViewManager/PoolPane.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, InputBase, Typography } from "@mui/material";
import AddIcon               from "@mui/icons-material/Add";
import AddCircleOutlineIcon  from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon        from "@mui/icons-material/ExpandMore";
import ExpandLessIcon        from "@mui/icons-material/ExpandLess";
import SearchIcon            from "@mui/icons-material/Search";
import ClearIcon             from "@mui/icons-material/Clear";
import WarningAmberIcon      from "@mui/icons-material/WarningAmber";
import HyperionToolTip       from "../../HyperionToolTip";
import "./PoolPane.css";

// ── Colour helpers ────────────────────────────────────────────────────────────
const getTypeColor = (model, customTheme) => {
  const t = customTheme;
  if (model === "submission")               return t.submissionColour;
  if (model === "submissions_under_review") return t.submissionsUnderReviewColour;
  if (model === "review")                   return t.reviewColour;
  if (model === "user")                     return t.userColour;
  return                                           t.mainColour;
};

const MODEL_LABEL = {
  submission:               "Submissions",
  submissions_under_review: "Under Review",
  review:                   "Reviews",
  user:                     "Users",
};

// Map view type → pool model key
const viewTypeToModel = (type) => {
  if (!type) return "submission";
  const t = String(type).toLowerCase();
  if (t === "submissions")              return "submission";
  if (t === "submissions_under_review") return "submissions_under_review";
  if (t === "reviews")                  return "review";
  if (t === "users")                    return "user";
  return "submission";
};

const normalizeLinked = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (s === "users") return "user";
  return s || "other";
};

// ── PoolItem ──────────────────────────────────────────────────────────────────
const PoolItem = ({ item, typeColor, isMissing, onAddToView }) => {
  const isHardcoded = item?.origin === "hardcoded";
  const label       = item.headerName || item.field;
  const fieldKey    = item.field;

  return (
    <HyperionToolTip
      title={`field: ${fieldKey}`}
      placement="left"
      variant="dark"
    >
      <div
        className={[
          "pp-item",
          isMissing   ? "pp-item--missing"   : "",
          isHardcoded ? "pp-item--hardcoded" : "",
        ].join(" ")}
        style={{ "--itemColor": typeColor }}
      >
        {/* Missing pulse bar */}
        {isMissing && <span className="pp-item-missing-bar" aria-hidden="true" />}

        {/* Name */}
        <div className="pp-item-body">
          {isMissing && (
            <WarningAmberIcon className="pp-item-warning-icon" />
          )}
          <span className="pp-item-name">{label}</span>
        </div>

        <div className="pp-item-right">
          {/* Origin badge */}
          <span
            className={`pp-badge ${isHardcoded ? "pp-badge--system" : "pp-badge--form"}`}
            title={isHardcoded ? "System field" : "Form field"}
          >
            {isHardcoded ? "sys" : "form"}
          </span>

          {/* Add button */}
          <button
            className="pp-add-btn"
            aria-label={`Add ${label} to view`}
            onClick={() => onAddToView(item)}
          >
            <AddCircleOutlineIcon style={{ fontSize: 15 }} />
          </button>
        </div>
      </div>
    </HyperionToolTip>
  );
};

// ── PoolSection ───────────────────────────────────────────────────────────────
const PoolSection = ({ model, items, customTheme, onAddToView, isOpen, onToggle }) => {
  const typeColor  = getTypeColor(model, customTheme);
  const label      = MODEL_LABEL[model] || (model.charAt(0).toUpperCase() + model.slice(1));

  const missing   = items.filter((p) => p?.missingInView === true);
  const available = items.filter((p) => p?.missingInView !== true);

  return (
    <div
      className={["pp-section", isOpen ? "pp-section--open" : ""].join(" ")}
      style={{ "--sectionColor": typeColor }}
    >
      {/* Section header */}
      <button className="pp-section-header" onClick={onToggle}>
        <span className="pp-section-label">{label}</span>

        <span className="pp-section-badges">
          {missing.length > 0 && (
            <span className="pp-section-count pp-section-count--missing" title="Missing from view">
              <WarningAmberIcon style={{ fontSize: 10 }} />
              {missing.length}
            </span>
          )}
          <span className="pp-section-count">{items.length}</span>
        </span>

        <span className="pp-section-chevron">
          {isOpen
            ? <ExpandLessIcon style={{ fontSize: 14 }} />
            : <ExpandMoreIcon style={{ fontSize: 14 }} />
          }
        </span>
      </button>

      {/* Body */}
      {isOpen && (
        <div className="pp-section-body">
          {/* Missing subsection */}
          {missing.length > 0 && (
            <>
              <div className="pp-subsection-label pp-subsection-label--missing">
                <WarningAmberIcon style={{ fontSize: 10 }} />
                Missing from view
              </div>
              {missing.map((p) => (
                <PoolItem
                  key={`${model}:missing:${p.sourceFormId || "na"}:${p.field}`}
                  item={p}
                  typeColor={typeColor}
                  isMissing
                  onAddToView={onAddToView}
                />
              ))}
              {available.length > 0 && (
                <div className="pp-subsection-divider" />
              )}
            </>
          )}

          {/* Available */}
          {available.map((p) => (
            <PoolItem
              key={`${model}:${p.sourceFormId || "na"}:${p.field}`}
              item={p}
              typeColor={typeColor}
              isMissing={false}
              onAddToView={onAddToView}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── PoolPane ──────────────────────────────────────────────────────────────────
export default function PoolPane({
  pool        = [],
  onAddToView = () => {},
  onAddBlank  = () => {},
  customTheme,
  activeView,
}) {
  const [filter, setFilter] = useState("");

  // ── Determine which section should be open based on active view type ───────
  const activeModel = useMemo(
    () => viewTypeToModel(activeView?.type),
    [activeView?.type]
  );

  const [openSection, setOpenSection] = useState(activeModel);

  // When the active view changes type, switch the open section
  useEffect(() => {
    setOpenSection(activeModel);
  }, [activeModel]);

  const toggleSection = (model) => {
    setOpenSection((prev) => (prev === model ? null : model));
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(
      (p) =>
        String(p?.headerName || "").toLowerCase().includes(q) ||
        String(p?.field      || "").toLowerCase().includes(q)
    );
  }, [pool, filter]);

  // ── Group by model ────────────────────────────────────────────────────────
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((p) => {
      const model = normalizeLinked(p?.linked);
      if (!map.has(model)) map.set(model, []);
      map.get(model).push(p);
    });
    // Sort groups: active model first, then alphabetical
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === activeModel) return -1;
      if (b === activeModel) return  1;
      return String(a).localeCompare(String(b));
    });
  }, [filtered, activeModel]);

  return (
    <Box className="cv-pane cv-pane--right">

      {/* Header */}
      <Box className="cv-pane-header">
        <Typography variant="subtitle2">Pool</Typography>
        <Button
          size="small"
          className="pp-blank-btn"
          onClick={onAddBlank}
          startIcon={<AddIcon style={{ fontSize: 13 }} />}
        >
          Blank
        </Button>
      </Box>

      {/* Search */}
      <div className="pp-search-wrap">
        <SearchIcon className="pp-search-icon" />
        <InputBase
          className="pp-search-input"
          placeholder="Filter columns…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          inputProps={{ "aria-label": "filter pool columns" }}
        />
        {filter && (
          <IconButton
            size="small"
            className="pp-search-clear"
            onClick={() => setFilter("")}
            aria-label="clear filter"
          >
            <ClearIcon style={{ fontSize: 13 }} />
          </IconButton>
        )}
      </div>

      {/* Groups */}
      <div className="pp-body cv-scroll">
        {groups.length === 0 && (
          <div className="pp-empty">
            {filter ? `No columns matching "${filter}"` : "All columns are in the view."}
          </div>
        )}

        {groups.map(([model, items]) => (
          <PoolSection
            key={model}
            model={model}
            items={items}
            customTheme={customTheme}
            onAddToView={onAddToView}
            isOpen={openSection === model}
            onToggle={() => toggleSection(model)}
          />
        ))}
      </div>
    </Box>
  );
}
